import { APITypesV1 } from "@cur8/api-client";
import {
  Button,
  Icon16Alert,
  Icon16Checkmark,
  Icon24ArrowRight,
  Typography,
} from "@cur8/maneki";
import { useAssignToEcgMutation } from "hooks/mutations/useAssignToEcgMutation";
import { useClearRoomControllerMutation } from "hooks/mutations/useClearRoomControllerMutation";
import { useCompleteVisitMutation } from "hooks/mutations/useCompleteVisitMutation";
import { useECGDeviceQuery } from "hooks/queries/useECGDeviceQuery";
import { assignErrorParam } from "lib/isEcgDeviceOffline";
import { useVisitQuery } from "hooks/queries/useVisitQuery";
import { useShowNekoLens } from "hooks/useShowNekoLens";
import { ecgScanLabel } from "lib/ecgScanLabel";
import { isDermaFollowUp } from "lib/isDermaFollowUp";
import { isReassignNoravEcg } from "lib/isReassignNoravEcg";
import { readScanStateFromVisit } from "lib/readScanStateFromVisit";
import { CheckInParams } from "lib/types/checkInParameters";
import { useCallback, useMemo } from "react";
import { paths } from "render/routes/paths";
import { InformationLayout } from "render/ui/layout/InformationLayout/InformationLayout";
import { PageFrameContent } from "render/ui/layout/PageFrameContent/PageFrameContent";
import { List } from "render/ui/presentation/List";
import { Link } from "render/ui/trigger/Link";
import styles from "../CheckInCompleteView/styles.module.sass";

type FollowUpEcgViewProps = {
  goTo(url: string): void;
} & CheckInParams;

export function FollowUpEcgView({
  goTo,
  patientId,
  visitId,
}: FollowUpEcgViewProps) {
  const clearRoomController = useClearRoomControllerMutation();
  const completeVisit = useCompleteVisitMutation();
  const assignToEcgMutation = useAssignToEcgMutation();
  const { data: visit } = useVisitQuery({
    patientId,
    visitId,
    options: { refetchInterval: 5000 },
  });
  const eCGDeviceQuery = useECGDeviceQuery({ patientId, visitId });
  const showNekoLens = useShowNekoLens({ patientId, visitId });

  const ecgScanState = readScanStateFromVisit("ecg", visit);

  const hasDermaExam = isDermaFollowUp(visit?.medicalExams);

  const hasNoravDevice =
    eCGDeviceQuery.data?.deviceType === APITypesV1.EcgType.EcgNorav;

  const assignmentStatus = eCGDeviceQuery.data?.assignmentStatus;
  const isReassign = isReassignNoravEcg(assignmentStatus, ecgScanState);
  const assignLabel = isReassign ? "Re-assign member" : "Assign to Norav";

  const handleAssignPress = useCallback(() => {
    if (isReassign) {
      goTo(paths.assignToNoravECGDeviceFollowUp.url({ patientId, visitId }));
      return;
    }
    if (assignmentStatus !== APITypesV1.DeviceAssignmentStatus.CanAssign) {
      return;
    }
    if (assignToEcgMutation.isPending) {
      return;
    }
    assignToEcgMutation.mutate(
      { patientId, visitId },
      {
        onSuccess: () => {
          eCGDeviceQuery.refetch();
        },
        onError: (error) => {
          goTo(
            paths.assignToNoravECGDeviceFollowUp.url({ patientId, visitId }) +
              `?assignError=${assignErrorParam(error)}`
          );
        },
      }
    );
  }, [
    assignmentStatus,
    assignToEcgMutation,
    eCGDeviceQuery,
    goTo,
    isReassign,
    patientId,
    visitId,
  ]);

  const goToNekoLensAuth = useCallback(() => {
    goTo(paths.nekoLensAuth.url({ patientId, visitId }));
  }, [goTo, patientId, visitId]);

  const goToHome = useCallback(async () => {
    try {
      await Promise.all([
        clearRoomController.mutateAsync({ patientId, visitId }),
        completeVisit.mutateAsync({ patientId, visitId }),
      ]);
      goTo(paths.root.url({}));
    } catch {
      // errors surface via useReporting.logError in each hook; stay on screen
    }
  }, [clearRoomController, completeVisit, goTo, patientId, visitId]);

  const isCheckingOut =
    clearRoomController.isPending || completeVisit.isPending;

  const showAnyItems = hasNoravDevice || (hasDermaExam && showNekoLens);

  return (
    <PageFrameContent>
      <InformationLayout
        content={
          <section className={styles.layout}>
            <Typography variant="display-s">Follow-up exams</Typography>
            {showAnyItems ? (
              <List>
                {hasNoravDevice ? (
                  <List.Item>
                    <div className={styles.scanItem}>
                      <div className={styles.itemTitle}>
                        <Typography variant="title-s">
                          {ecgScanLabel(eCGDeviceQuery.data?.deviceType)}
                        </Typography>
                        <div className={styles.itemActions}>
                          <Link onPress={handleAssignPress}>{assignLabel}</Link>
                          <Typography variant="body-m" color="subtle">
                            ({patientId})
                          </Typography>
                        </div>
                      </div>
                      {ecgScanState != null ? (
                        <ScanStatusBadge scanState={ecgScanState} />
                      ) : undefined}
                    </div>
                  </List.Item>
                ) : undefined}
                {hasDermaExam && showNekoLens ? (
                  <List.Item>
                    <div className={styles.scanItem}>
                      <div className={styles.itemTitle}>
                        <Typography variant="title-s">Derma</Typography>
                        <div className={styles.itemActions}>
                          <Link onPress={goToNekoLensAuth}>Show QR code</Link>
                        </div>
                      </div>
                    </div>
                  </List.Item>
                ) : undefined}
              </List>
            ) : undefined}
            <div className={styles.divider} />
            <section className={styles.checkoutSection}>
              <div className={styles.checkoutText}>
                <Typography variant="title-l">Ready to check out?</Typography>
                <Typography variant="body-m" color="subtle">
                  Check out if all examinations are complete
                </Typography>
              </div>
              <Button
                onPress={goToHome}
                fillContainer
                icon={<Icon24ArrowRight />}
                state={isCheckingOut ? "loading" : undefined}
              >
                Check Out
              </Button>
            </section>
          </section>
        }
      />
    </PageFrameContent>
  );
}

type ScanStatusBadgeHighlight = "success" | "error";

function ScanStatusBadge({
  scanState,
}: {
  scanState: APITypesV1.ImmutableScanState;
}) {
  const { label, highlight } = useMemo<{
    label: string;
    highlight?: ScanStatusBadgeHighlight;
  }>(() => {
    switch (scanState) {
      case APITypesV1.ImmutableScanState.Finished:
        return { label: "Completed", highlight: "success" };
      case APITypesV1.ImmutableScanState.Inprogress:
        return { label: "In progress", highlight: undefined };
      case APITypesV1.ImmutableScanState.Available:
        return { label: "Ready", highlight: undefined };
      case APITypesV1.ImmutableScanState.Failed:
        return { label: "Missing", highlight: "error" };
      default:
        return { label: "", highlight: undefined };
    }
  }, [scanState]);

  return (
    <div className={styles.scanStatusBadge} data-highlight={highlight}>
      {highlight === "success" && <Icon16Checkmark />}
      {highlight === "error" && <Icon16Alert />}
      <Typography variant="eyebrow-m" as="span">
        {label}
      </Typography>
    </div>
  );
}
